/**
 * The ONE entity display-name resolution order, shared by the legacy Select2Util display chain and the
 * Picker stack's Read/Print display so the Select2 → Picker migration cannot change what a field paints.
 *
 * Two record shapes are why this can never be a flat `pRecord[Field]` read:
 *
 *   - **User.** The table carries no `Name` column (NameFirst, NameLast, Phone, Email, LoginID, …), so an
 *     `Entity: 'User'` descriptor with no display field — the shared LADOTD "Sampled By" / "Created By"
 *     header fields, injected into ~152 forms — reads undefined and paints blank.
 *   - **A dotted display field** (`MixMetadata.MixName`). `pRecord['MixMetadata.MixName']` is undefined;
 *     the path has to be walked hop by hop.
 *
 * The two callers differ ONLY in what they show when the record has nothing, which is what lets one
 * implementation serve both: Select2Util passes its legacy `Unnamed <Entity> #<id>` placeholder as
 * pUnnamedFallback, while PickerDisplay passes none, takes '' and paints unnamedEntityLabel's
 * `<Entity> #<id>` itself — the Picker stack's label for a record with no name, shared with the Edit
 * picker so both read the same.
 *
 * @module _EntityDisplayName
 */

/**
 * Walk a dotted path across a record, hasOwnProperty-guarded at each hop. A missing hop yields null
 * rather than throwing.
 *
 * @param {Record<string, any>} pRecord - The entity record.
 * @param {string} pPath - The dotted field path.
 * @return {any}
 */
function resolveDottedValue(pRecord, pPath)
{
	let tmpValue = /** @type {any} */ (pRecord);
	for (const tmpField of pPath.split('.'))
	{
		if (!tmpValue || !Object.prototype.hasOwnProperty.call(tmpValue, tmpField))
		{
			return null;
		}
		tmpValue = tmpValue[tmpField];
	}
	return tmpValue;
}

/**
 * A User's display name, composed from the name parts the table actually has. Joins only the parts that
 * are present, so a record missing both yields '' — and the caller's fallback — rather than the literal
 * "undefined undefined" the legacy template produced.
 *
 * @param {Record<string, any>} pRecord - The User record.
 * @return {string}
 */
function composeUserName(pRecord)
{
	return [ pRecord.NameFirst, pRecord.NameLast ]
		.filter((pPart) => (pPart !== undefined && pPart !== null && String(pPart).trim() !== ''))
		.join(' ');
}

/**
 * The display text for an entity record. Pure — the record is never written to.
 *
 * @param {string} pEntity - The entity type the record belongs to.
 * @param {Record<string, any>} pRecord - The entity record.
 * @param {Record<string, any>} [pOptions] - Display config; TextField or EntityDisplayField names the
 *   display field (a dotted path is walked).
 * @param {() => string} [pUnnamedFallback] - What to show when the record has nothing; omitted = ''.
 *
 * @return {any} - The display text.
 */
function resolveEntityDisplayName(pEntity, pRecord, pOptions = {}, pUnnamedFallback)
{
	if (!pRecord || typeof pRecord !== 'object' || Array.isArray(pRecord))
	{
		return '';
	}
	const fFallback = () => ((typeof pUnnamedFallback === 'function') ? pUnnamedFallback() : '');
	const tmpDisplayField = pOptions.TextField || pOptions.EntityDisplayField;
	if (tmpDisplayField && tmpDisplayField.includes('.'))
	{
		return resolveDottedValue(pRecord, tmpDisplayField) || fFallback();
	}
	if (tmpDisplayField)
	{
		// No fallback on this branch, matching the legacy chain: an explicitly named field that is empty
		// stays empty rather than being replaced by a placeholder.
		return pRecord[tmpDisplayField];
	}
	if (pEntity === 'User')
	{
		return composeUserName(pRecord) || fFallback();
	}
	return pRecord.Name || fFallback();
}

/**
 * The noun for an Organization, which plays a different role on each field (Producer, Contractor, Source, …):
 * a one-word field label names the role, a longer label that mentions a producer reads `Producer`, and
 * anything else reads `Organization`.
 *
 * @param {string} [pFieldLabel] - The label of the field showing the record.
 * @return {string}
 */
function organizationLabel(pFieldLabel)
{
	const tmpLabel = String(pFieldLabel || '').trim();
	if (/^[A-Za-z]+$/.test(tmpLabel))
	{
		return tmpLabel;
	}
	if (/producer/i.test(tmpLabel))
	{
		return 'Producer';
	}
	return 'Organization';
}

/**
 * The user-facing noun for an entity: the table name split into words (`MixSpecification` →
 * `Mix Specification`), or for an Organization the role its field label names (organizationLabel).
 *
 * @param {string} pEntity - The entity type.
 * @param {string} [pFieldLabel] - The label of the field showing the record.
 * @return {string}
 */
function entityLabel(pEntity, pFieldLabel)
{
	if (pEntity === 'Organization')
	{
		return organizationLabel(pFieldLabel);
	}
	return String(pEntity || '').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
}

/**
 * What a record with no display name shows in its place: `<entity label> #<record ID>`, e.g.
 * `Document #37945` or `Contractor #3456`. '' when there is no ID to show.
 *
 * @param {string} pEntity - The entity type.
 * @param {any} pID - The record's ID.
 * @param {string} [pFieldLabel] - The label of the field showing the record.
 * @return {string}
 */
function unnamedEntityLabel(pEntity, pID, pFieldLabel)
{
	if (pID === undefined || pID === null || String(pID).trim() === '')
	{
		return '';
	}
	return `${entityLabel(pEntity, pFieldLabel)} #${pID}`;
}

module.exports = { resolveEntityDisplayName, entityLabel, unnamedEntityLabel };
